import { MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { Expression, Map as MBMap } from 'mapbox-gl'

import {
  MapLayerStyle,
  MapLayerStyles,
} from '@app/contexts/ModuleMapContexts/GenericMapContext/GenericMapContext.types'
import { RiskMapContext } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { handleClearRemovedLayers } from '@app/contexts/ModuleMapContexts/utils/layers'
import { VectorTileset } from '../../LayersControl'
import { LegendsData } from './AddLayersDialog'
import {
  addIconLayer,
  addLineLayer,
  addPointLayer,
  addRasterLayer,
  addVectorLayer,
} from './LayerTypes'
import { AssetHazardInstances, VulnerabilityData } from './RiskMapView'
import {
  InformationLayerPalette,
  InformationLayerPalettes,
  VisibleAssets,
  byAssetIdIn,
  generateVulnerabilityPalette,
  getMaxVulnerabilityState,
  getSortKeyExpression,
} from './RiskMapView.utilities'

export function renderLayersInOrder(
  map: MBMap,
  layers: MapLayer[],
  layerStyles: MapLayerStyles,
  assetIdsToDisplay: VisibleAssets,
  vulnerabilityData: VulnerabilityData,
  legendsData: LegendsData,
  informationLayerPalettes: InformationLayerPalettes,
  addRegionMasksLayers: RiskMapContext['addRegionMasksLayers'],
  regionMasks: RiskMapContext['regionMasks'],
  hasFilters: boolean,
  applyVulnerabilityData: boolean,
) {
  // clearMap({ map })
  handleClearRemovedLayers({ map, layers })
  const hazardLayers = layers.filter((layer) => layer.layerType === 'hazard')

  const mapStyle = map.getStyle()
  if (!mapStyle) return

  const firstDrawAreaLayer = mapStyle.layers.filter((layer) => layer.id.includes('gl-draw'))[0]

  let lastId: string

  if (firstDrawAreaLayer) {
    lastId = firstDrawAreaLayer.id
  } else {
    lastId = ''
  }

  const uiTilesets = map.getStyle().layers.filter((layer) => layer.id.includes('urbanintelligence'))

  for (const tileset of uiTilesets) {
    if (
      tileset.id.includes('_vector') &&
      !layers.some((layer) => layer.tilesets.includes(tileset))
    ) {
      map.removeLayer(tileset.id)
    }
  }

  layers.forEach((layer) => {
    const totalTilesets = layer.infoLookupTileset
      ? [...layer.tilesets, ...layer.infoLookupTileset]
      : layer.tilesets

    return totalTilesets.forEach((tileset) => {
      const foundTileSet = uiTilesets.find((l) => l.id === tileset.id)
      const shouldUpdate = !!map.getSource(tileset.id) && !!foundTileSet
      const lookupTileSet =
        layer?.infoLookupTileset?.length &&
        layer.infoLookupTileset.find((lookupTileset) => lookupTileset.id === tileset.id)
      if (lookupTileSet) {
        renderHazardInfoLayer({
          map,
          layer,
          layerStyles,
          vectorTileset: lookupTileSet as VectorTileset,
          isVisible: layer.visible,
          beforeId: lastId,
          legendsData,
          shouldUpdate,
        })
        if (lastId) map.moveLayer(tileset.id, lastId)
        lastId = tileset.id
        return
      }
      switch (layer.layerType) {
        case 'asset':
          renderAssetLayer(
            map,
            layer,
            layerStyles,
            assetIdsToDisplay[layer.type],
            hasFilters,
            tileset as VectorTileset,
            layer.visible,
            lastId,
            vulnerabilityData[layer.type],
            legendsData,
            hazardLayers,
            shouldUpdate,
            applyVulnerabilityData,
          )
          if (lastId) map.moveLayer(tileset.id, lastId)
          lastId = tileset.id
          break

        case 'information':
          renderInformationLayer(
            map,
            layerStyles,
            tileset as VectorTileset,
            layer.visible,
            lastId,
            informationLayerPalettes[layer.legend],
            shouldUpdate,
          )
          if (lastId) map.moveLayer(tileset.id, lastId)
          lastId = tileset.id
          break

        case 'hazard':
          addRasterLayer({
            map,
            id: tileset.id,
            isVisible: layer.visible,
            beforeId: lastId,
            shouldUpdate,
          })
          if (lastId) map.moveLayer(tileset.id, lastId)
          lastId = tileset.id
          break
      }
    })
  })

  if (regionMasks && regionMasks.length) addRegionMasksLayers({ regionMasks: regionMasks, map })

  // Put the selection tool layer on top of all layers
  const selectionLayer = map.getLayer('selectionToolLineLayer')
  if (selectionLayer) map.moveLayer(selectionLayer.id)

  const selectionAreaLayer = map.getLayer('selectionToolAreaLayer')
  if (selectionAreaLayer) map.moveLayer(selectionAreaLayer.id)
}

function renderAssetLayer(
  map: MBMap,
  layer: MapLayer,
  layerStyles: MapLayerStyles,
  assetIds: { [asset_id: number]: boolean },
  hasFilters: boolean,
  vectorTileset: VectorTileset,
  isVisible: boolean,
  beforeId: string,
  assetHazardInstances: AssetHazardInstances,
  legendsData: LegendsData,
  hazardLayers: MapLayer[],
  shouldUpdate: boolean,
  applyVulnerabilityData: boolean,
) {
  // k: v of assetId to style in pallete
  const vulnerabilityData = getMaxVulnerabilityState(hazardLayers, assetHazardInstances)

  // Indexed pallete for the layer, minimum of 1 color:
  const vulnerabilityPalette =
    hazardLayers.length && applyVulnerabilityData
      ? generateVulnerabilityPalette(legendsData, layer.legend ?? layer.type)
      : ['#0b2a48']

  // Build an expression to order features from most to least vulnerable:
  const sortKeyExpression = getSortKeyExpression(vulnerabilityData)

  function getOpacityExpression(
    visibilityExpression: Expression,
    visibleOpacity = 1,
    notVisibleOpacity = 0.3,
  ): Expression {
    return ['case', visibilityExpression, visibleOpacity, notVisibleOpacity]
  }

  let visibilityExpression: Expression | boolean = false
  if (hasFilters) {
    visibilityExpression = ['boolean', isVisible && byAssetIdIn(assetIds)]
  }

  const fillOpacityExpression =
    hasFilters && visibilityExpression
      ? getOpacityExpression(visibilityExpression, 0.8)
      : (['literal', 0.8] as Expression)

  const lineOpacityExpression =
    hasFilters && visibilityExpression
      ? getOpacityExpression(visibilityExpression)
      : (['literal', 1] as Expression)

  const idAttribute = 'asset_id'

  generateLayer({
    map,
    layerStyles,
    vectorTileset,
    beforeId,
    idAttribute,
    isVisible,
    vulnerabilityData,
    vulnerabilityPalette,
    lineOpacityExpression,
    fillOpacityExpression,
    assetIds,
    sortKeyExpression,
    hasFilters,
    shouldUpdate,
  })
}

function renderInformationLayer(
  map: MBMap,
  layerStyles: MapLayerStyles,
  vectorTileset: VectorTileset,
  isVisible: boolean,
  beforeId: string,
  informationLayerPalette?: InformationLayerPalette,
  shouldUpdate = false,
) {
  let colorExpression: Expression
  if (!informationLayerPalette) colorExpression = ['to-color', '#888888']
  else
    switch (informationLayerPalette.type) {
      case 'continuous':
        {
          colorExpression = [
            'interpolate-lab',
            ['linear'],
            ['to-number', ['get', informationLayerPalette.dataColumn]],
          ]

          informationLayerPalette.palette.forEach((stop) => {
            colorExpression.push(stop.value)
            colorExpression.push(stop.color)
          })
        }
        break

      case 'categorical':
        {
          colorExpression = [
            'to-color',
            [
              'at',
              ['number', ['get', informationLayerPalette.dataColumn], 0], // If it's not a number return 0
              ['literal', informationLayerPalette.palette],
            ],
          ]
        }
        break

      case 'keyValue':
        {
          colorExpression = [
            'to-color',
            [
              'get',
              ['get', informationLayerPalette.dataColumn],
              ['literal', informationLayerPalette.palette],
            ],
          ]
        }
        break
    }

  const fillOpacityExpression: Expression = ['literal', vectorTileset.type == 'point' ? 1 : 0.3]
  const lineOpacityExpression: Expression = ['literal', 1]

  generateLayer({
    map,
    layerStyles,
    vectorTileset,
    beforeId,
    isVisible,
    colorExpression,
    lineOpacityExpression,
    fillOpacityExpression,
    shouldUpdate,
  })
}

function renderHazardInfoLayer({
  map,
  layer,
  layerStyles,
  vectorTileset,
  isVisible,
  beforeId,
  legendsData,
  shouldUpdate,
}: {
  map: MBMap
  layer: MapLayer
  layerStyles: MapLayerStyles
  vectorTileset: VectorTileset
  isVisible: boolean
  beforeId: string
  legendsData: LegendsData
  shouldUpdate: boolean
}) {
  const vulnerabilityPalette = generateVulnerabilityPalette(legendsData, layer.legend ?? layer.type)
  const colorExpression = ['to-color', 'transparent'] as Expression
  const updatedTileset = {
    ...vectorTileset,
    sourceLayer: vectorTileset.id.replace('urbanintelligence.', ''),
  }
  const sortKeyExpression = ['number', 0] as Expression

  function getOpacity(
    visibilityExpression: Expression,
    visibleOpacity = 1,
    notVisibleOpacity = 0.3,
  ): Expression {
    return ['case', visibilityExpression, visibleOpacity, notVisibleOpacity]
  }

  const visibilityExpression: Expression | boolean = false

  const fillOpacityExpression = visibilityExpression
    ? getOpacity(visibilityExpression, 0.8)
    : (['literal', 0.8] as Expression)

  const lineOpacityExpression = visibilityExpression
    ? getOpacity(visibilityExpression)
    : (['literal', 1] as Expression)

  generateLayer({
    map,
    layerStyles,
    vectorTileset: updatedTileset,
    beforeId,
    isVisible,
    vulnerabilityPalette,
    colorExpression,
    lineOpacityExpression,
    fillOpacityExpression,
    sortKeyExpression,
    shouldUpdate,
  })
}

function generateLayer({
  map,
  layerStyles,
  vectorTileset,
  beforeId,
  idAttribute,
  isVisible,
  vulnerabilityData,
  vulnerabilityPalette,
  colorExpression,
  lineOpacityExpression,
  fillOpacityExpression,
  assetIds,
  sortKeyExpression,
  hasFilters = false,
  shouldUpdate = false,
}: {
  map: MBMap
  layerStyles: MapLayerStyles
  vectorTileset: VectorTileset
  beforeId: string
  idAttribute?: string
  isVisible: boolean
  vulnerabilityData?: Map<string, number>
  vulnerabilityPalette?: string[]
  colorExpression?: Expression
  lineOpacityExpression?: Expression
  fillOpacityExpression?: Expression
  assetIds?: { [asset_id: number]: boolean }
  sortKeyExpression?: Expression
  hasFilters?: boolean
  shouldUpdate?: boolean
}) {
  const layerStyle: MapLayerStyle = vectorTileset.style
    ? layerStyles[vectorTileset.style]
    : { paint: {}, layout: {} }
  switch (vectorTileset.type) {
    case 'line': {
      addLineLayer({
        map,
        layerStyle,
        id: vectorTileset.id,
        beforeId,
        idAttribute,
        sourceLayer: vectorTileset.sourceLayer,
        isVisible,
        vulnerabilityData,
        vulnerabilityPalette,
        colorExpression,
        lineOpacityExpression,
        assetIds,
        sortKeyExpression,
        hasFilters,
        shouldUpdate,
      })
      return
    }

    case 'vector': {
      addVectorLayer({
        map,
        layerStyle,
        tileset: vectorTileset,
        beforeId,
        idAttribute,
        isVisible,
        vulnerabilityData,
        vulnerabilityPalette,
        colorExpression,
        fillOpacityExpression,
        assetIds,
        sortKeyExpression,
        hasFilters,
        shouldUpdate,
      })
      return
    }

    case 'point': {
      addPointLayer({
        map,
        layerStyle,
        id: vectorTileset.id,
        beforeId,
        idAttribute,
        sourceLayer: vectorTileset.sourceLayer,
        isVisible,
        vulnerabilityData,
        vulnerabilityPalette,
        colorExpression,
        fillOpacityExpression,
        assetIds,
        sortKeyExpression,
        hasFilters,
        shouldUpdate,
      })
      return
    }

    case 'icon': {
      addIconLayer({
        map,
        layerStyle,
        id: vectorTileset.id,
        beforeId,
        idAttribute,
        isVisible,
        sourceLayer: vectorTileset.sourceLayer,
        icon: vectorTileset.icon,
        vulnerabilityData,
        vulnerabilityPalette,
        colorExpression,
        assetIds,
        sortKeyExpression,
        hasFilters,
        shouldUpdate,
      })
      return
    }
    default:
      return <></>
  }
}
